// Die Art eines Termins - Event, Stammtisch oder Ausfahrt.
//
// Reine Logik, ohne React, ohne Firebase. Backend-Spiegel:
// functions/shared/eventKind.js (synchron halten - die firestore.rules
// pruefen gegen dieselbe Whitelist, Termine schreibt der Client direkt).

export const EVENT_KIND = {
  EVENT: 'event',
  STAMMTISCH: 'stammtisch',
  AUSFAHRT: 'ausfahrt',
};

/**
 * Reihenfolge der Abschnitte in der Monatsuebersicht.
 *
 * Ausfahrten zuerst: Sie brauchen die meiste Planung (Anfahrt, Tank, Zeit),
 * ein Stammtisch am wenigsten. Wer die Reihenfolge aendert, aendert damit
 * auch die Uebersicht - sie ist die einzige Quelle dafuer.
 */
export const ALL_EVENT_KINDS = [EVENT_KIND.AUSFAHRT, EVENT_KIND.STAMMTISCH, EVENT_KIND.EVENT];

export const EVENT_KIND_LABELS = {
  [EVENT_KIND.EVENT]: 'Event',
  [EVENT_KIND.STAMMTISCH]: 'Stammtisch',
  [EVENT_KIND.AUSFAHRT]: 'Ausfahrt',
};

export const EVENT_KIND_LABELS_PLURAL = {
  [EVENT_KIND.EVENT]: 'Events',
  [EVENT_KIND.STAMMTISCH]: 'Stammtische',
  [EVENT_KIND.AUSFAHRT]: 'Ausfahrten',
};

export const EVENT_KIND_ICONS = {
  [EVENT_KIND.EVENT]: '📅',
  [EVENT_KIND.STAMMTISCH]: '🍻',
  [EVENT_KIND.AUSFAHRT]: '🚗',
};

/**
 * Die Art eines Termins - auch bei Bestandsterminen ohne das Feld.
 *
 * Zwei Rueckfallebenen, in dieser Reihenfolge:
 * 1. Ein Termin mit `tourId` kommt aus dem Tourenplaner und IST eine Ausfahrt.
 *    Das ist keine Vermutung, sondern folgt aus seiner Herkunft.
 * 2. Alles andere ist ein Event. Ein Stammtisch laesst sich nicht erraten -
 *    ihn traegt der Organisator ein.
 *
 * Deshalb ist `kind` bewusst optional: Kein Migrationslauf ueber die
 * Bestandstermine, keine Zeile, die ploetzlich falsch einsortiert ist.
 */
export function normalizeEventKind(event) {
  const value = typeof event?.kind === 'string' ? event.kind.trim() : '';
  if (ALL_EVENT_KINDS.includes(value)) return value;
  if (event?.tourId) return EVENT_KIND.AUSFAHRT;
  return EVENT_KIND.EVENT;
}

/** Beschriftung eines Termins fuer Karten und Zeilen. */
export function eventKindLabel(event) {
  return EVENT_KIND_LABELS[normalizeEventKind(event)];
}

/**
 * Sentinel-Wert des Kategorie-Filters: „Alle Arten".
 *
 * Bewusst ein eigener Wert und nicht null/undefined: Er steckt im State der
 * Chip-Leiste und im `aria-pressed` eines Knopfes - ein fehlender Wert waere
 * dort nicht vom „noch nichts gewaehlt" zu unterscheiden. Doppelte
 * Unterstriche, damit er nie mit einer echten Art kollidiert.
 */
export const EVENT_KIND_ALL = '__all__';

/**
 * Termine einer Art - oder alle.
 *
 * Bewusst clientseitig: Die Terminseite laedt ihren Zeitraum ohnehin
 * vollstaendig, und eine Firestore-Abfrage mit `kind ==` neben dem
 * array-contains auf `audiences` braeuchte einen zusaetzlichen
 * zusammengesetzten Index fuer null Gewinn.
 */
export function filterEventsByKind(events, kind) {
  if (!kind || kind === EVENT_KIND_ALL) return events ?? [];
  return (events ?? []).filter((event) => normalizeEventKind(event) === kind);
}

/**
 * Anzahl je Art - fuer die Zaehler an den Filter-Chips.
 *
 * Gezaehlt wird auf derselben Liste, die auch gefiltert wird. Eine Zahl, die
 * zu der gefilterten Ansicht nicht passt, schickt den Nutzer in die Irre.
 */
export function countEventsByKind(events) {
  const counts = Object.fromEntries(ALL_EVENT_KINDS.map((kind) => [kind, 0]));
  for (const event of events ?? []) {
    counts[normalizeEventKind(event)] += 1;
  }
  return counts;
}

/**
 * Termine nach Art gruppiert, in der Reihenfolge von ALL_EVENT_KINDS.
 *
 * Leere Arten fallen heraus: Eine Ueberschrift „Stammtische" ueber einer
 * leeren Liste behauptet, es gaebe dort etwas zu sehen.
 */
export function groupEventsByKind(events) {
  const groups = new Map(ALL_EVENT_KINDS.map((kind) => [kind, []]));
  for (const event of events ?? []) {
    groups.get(normalizeEventKind(event)).push(event);
  }
  return ALL_EVENT_KINDS.map((kind) => ({
    kind,
    label: EVENT_KIND_LABELS_PLURAL[kind],
    icon: EVENT_KIND_ICONS[kind],
    events: groups.get(kind),
  })).filter((group) => group.events.length > 0);
}
